import http from "@/services/http";

/**
 * Lista os medicamentos já registrados de um paciente (tabela patient_medications).
 *
 * @param {number|string} patientId
 * @returns {Promise<object[]>} lista de patient_medications (`{ id, name, dosage, ... }`)
 */
export async function getMedicationsByPatient(patientId) {
  const { data } = await http.get(
    `/api/patient-medications/get-by-patient/${patientId}`,
  );
  return data?.status === "success" ? data.data || [] : [];
}

/**
 * Busca medicamentos na API externa (reaproveita /api/prescription/search-medication).
 * Normaliza os candidatos numa lista pronta para exibição/seleção.
 *
 * @param {string} texto
 * @returns {Promise<Array<{name:string, dosage:string|null, presentation:string|null,
 *   tuss_code:string|null, label:string}>>}
 */
export async function searchMedications(texto) {
  const { data } = await http.post("/api/prescription/search-medication", {
    texto,
  });

  return (data?.candidatos || []).map((c) => ({
    name: c.nome,
    dosage: c.dosagem || null,
    presentation: c.apresentacao || null,
    tuss_code: c.id_tuss || null,
    label: [c.nome, c.dosagem, c.apresentacao].filter(Boolean).join(" — "),
  }));
}

/**
 * Adiciona um ou vários medicamentos de uma vez ao paciente.
 *
 * @param {number|string} patientId
 * @param {object[]} medications lista no formato do `searchMedications`
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function createMedications(patientId, medications) {
  return http.post("/api/patient-medications/create", {
    patient_id: patientId,
    medications: medications.map((m) => ({
      name: m.name,
      dosage: m.dosage || null,
      frequency: m.frequency || null,
      tuss_code: m.tuss_code || null,
      snomed_code: m.snomed_code || null,
      notes: m.notes || null,
    })),
  });
}

/**
 * Atualiza um medicamento (ex.: status de uso).
 *
 * @param {number} id
 * @param {object} payload ex.: { status: "using" | "stopped" | "unknown" }
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function updateMedication(id, payload) {
  return http.put(`/api/patient-medications/update/${id}`, payload);
}

/**
 * Remove um medicamento do paciente (soft delete).
 *
 * @param {number} id
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function deleteMedication(id) {
  return http.delete(`/api/patient-medications/delete/${id}`);
}
